/**
 * Log-safe view of a caught error.
 *
 * Server logs (Vercel, Trigger, Convex) sit outside the user's silo. Raw
 * errors carry their message, and provider/SDK errors routinely echo the
 * request (email bodies, prompts, addresses, tokens) or carry the response
 * body. Log `logSafeError(error)` instead of `error`, and
 * `logSafeErrorMessage(error)` instead of `error.message` / `String(error)`.
 *
 * What survives: error type, HTTP status, code, a sanitized and truncated
 * message (dropped entirely for provider/HTTP errors, whose messages echo
 * requests), the cause's type/code, and stack frames without the message line.
 * Convex function failures also keep the function name, request id, error
 * class and validator path, never the value dumps (see ./convexError).
 *
 * `scripts/ci/validate-log-hygiene.mjs` enforces this in server paths.
 */
import { convexFunctionOf, parseConvexServerError } from "./convexError.ts";
import { logErrorEvidence, logSafeLabel } from "./redact.ts";

const MAX_MESSAGE = 160;
const MAX_FRAMES = 6;
const V8_FRAME = /^at .+/;

export type SafeLogError = {
  errorType: string;
  errorStatus?: number;
  errorCode?: string;
  message?: string;
  causeType?: string;
  causeCode?: string;
  stack?: string;
  convexFunction?: string;
  convexRequestId?: string;
  /** Argument-validator path of a rejected Convex call, e.g. `.patch.scoreList[0].id`. */
  convexPath?: string;
};

/** Log context: ids, counts, codes and flags only. Strings that are not id-shaped are redacted. */
export type SafeLogContext = Readonly<Record<string, string | number | boolean | null | undefined>>;

const ID_SHAPED = /^[A-Za-z0-9_.:/#-]{1,128}$/;

/**
 * Strips content a message may echo: emails, URLs (query and path), bearer
 * tokens and long opaque strings, file names, quoted spans, long digit runs (phones,
 * card/account numbers) and anything after an embedded JSON/HTML body.
 */
export function sanitizeLogMessage(message: string): string {
  let text = message.replace(/\s+/g, " ").trim();
  const bodyStart = text.search(/[{[<]/);
  if (bodyStart >= 0) text = `${text.slice(0, bodyStart).trimEnd()} [body]`;
  text = text
    .replace(/[\w.+-]+@[\w-]+(\.[\w-]+)+/g, "[email]")
    .replace(/\b[a-z][a-z0-9+.-]*:\/\/\S+/gi, "[url]")
    .replace(/[^\s/\\]+\.(pdf|docx?|xlsx?|pptx?|csv|txt|rtf|md|eml|msg|ics|vcf|zip|png|jpe?g|gif|heic|webp|mp3|m4a|wav|mp4|mov)\b/gi, "[file].$1")
    .replace(/\b(bearer|basic|token|key|secret|password|authorization)([=:\s]+)\S+/gi, "$1$2[redacted]")
    .replace(/(["'`“‘]).*?(["'`”’])/g, "[quoted]")
    .replace(/\b[A-Za-z0-9_\-+/=]{24,}\b/g, "[opaque]")
    .replace(/\+?\d[\d\s().-]{6,}\d/g, "[number]");
  return text.length > MAX_MESSAGE ? `${text.slice(0, MAX_MESSAGE)}…` : text;
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === "object" ? value as Record<string, unknown> : null;
}

/** Provider/HTTP failures: their message is where request echoes live. */
function carriesRemoteBody(record: Record<string, unknown>): boolean {
  return record.status !== undefined
    || record.statusCode !== undefined
    || record.response !== undefined
    || record.body !== undefined
    || record.error !== undefined;
}

function stackFrames(stack: unknown): string | undefined {
  if (typeof stack !== "string") return undefined;
  const frames = stack
    .split("\n")
    .map((line) => line.trim())
    .filter((line) => V8_FRAME.test(line))
    .slice(0, MAX_FRAMES)
    .map((line) => line.slice(3).replace(/\?\S*/g, ""));
  return frames.length ? frames.join(" < ") : undefined;
}

/** `ConvexError` carries its code in `data`, not on the error. */
function errorEvidence(error: unknown, record: Record<string, unknown>) {
  const evidence = logErrorEvidence(error);
  const data = asRecord(record.data);
  if (evidence.errorCode === undefined && data && typeof data.code === "string") evidence.errorCode = logSafeLabel(data.code);
  return evidence;
}

type ConvexFailure = { message?: string; convexFunction?: string; convexRequestId?: string; convexPath?: string };

/** The safe parts of a Convex function failure, or null for any other error. */
function convexFailure(record: Record<string, unknown>): ConvexFailure | null {
  const parts = typeof record.message === "string" ? parseConvexServerError(record.message) : null;
  const convexFunction = convexFunctionOf(record);
  if (!parts && !convexFunction) return null;
  const failure: ConvexFailure = {};
  if (convexFunction) failure.convexFunction = convexFunction;
  if (parts) {
    failure.convexRequestId = parts.requestId;
    if (parts.path) failure.convexPath = parts.path;
    const detail = parts.detail ? sanitizeLogMessage(parts.detail) : "";
    failure.message = [parts.kind ?? "Server Error", detail].filter(Boolean).join(": ");
  } else if (typeof record.message === "string" && record.message) {
    failure.message = sanitizeLogMessage(record.message);
  }
  return failure;
}

/** Sanitized message only (for `${...}` and `message:` slots). */
export function logSafeErrorMessage(error: unknown): string {
  const record = asRecord(error);
  if (!record) {
    if (typeof error === "string") return sanitizeLogMessage(error);
    return error === undefined || error === null ? String(error) : typeof error;
  }
  const evidence = errorEvidence(error, record);
  const status = evidence.errorStatus !== undefined ? ` ${evidence.errorStatus}` : "";
  const code = evidence.errorCode && evidence.errorCode !== "redacted" ? ` ${evidence.errorCode}` : "";
  const convex = carriesRemoteBody(record) ? null : convexFailure(record);
  if (convex) {
    const where = [convex.convexFunction, convex.convexPath && `at ${convex.convexPath}`, convex.convexRequestId && `[request ${convex.convexRequestId}]`].filter(Boolean).join(" ");
    return `${evidence.errorType}${status}${code}: Convex${where ? ` ${where}` : ""}${convex.message ? `: ${convex.message}` : ""}`;
  }
  if (carriesRemoteBody(record) || typeof record.message !== "string") {
    return `${evidence.errorType}${status}${code}`;
  }
  return `${evidence.errorType}${status}${code}: ${sanitizeLogMessage(record.message)}`;
}

/** Structured, content-free error for a log argument. */
export function logSafeError(error: unknown): SafeLogError {
  const record = asRecord(error);
  if (!record) {
    return typeof error === "string"
      ? { errorType: "string", message: sanitizeLogMessage(error) }
      : { errorType: error === null ? "null" : typeof error };
  }
  const safe: SafeLogError = { ...errorEvidence(error, record) };
  const convex = carriesRemoteBody(record) ? null : convexFailure(record);
  if (convex) Object.assign(safe, convex);
  else if (!carriesRemoteBody(record) && typeof record.message === "string" && record.message) {
    safe.message = sanitizeLogMessage(record.message);
  }
  const cause = asRecord(record.cause);
  if (cause) {
    const causeEvidence = logErrorEvidence(cause);
    safe.causeType = causeEvidence.errorType;
    if (causeEvidence.errorCode) safe.causeCode = causeEvidence.errorCode;
  }
  const stack = stackFrames(record.stack);
  if (stack) safe.stack = stack;
  return safe;
}

/** Keeps id-shaped strings, numbers and flags; anything else becomes "redacted". */
export function logSafeContext(context: SafeLogContext): Record<string, string | number | boolean | null> {
  const out: Record<string, string | number | boolean | null> = {};
  for (const [key, value] of Object.entries(context)) {
    if (value === undefined) continue;
    const safeKey = logSafeLabel(key);
    if (typeof value === "string") out[safeKey] = ID_SHAPED.test(value) && !value.includes("@") ? value : "redacted";
    else out[safeKey] = typeof value === "number" && !Number.isFinite(value) ? null : value;
  }
  return out;
}

/** `console.error(tag, safeError, safeContext)` in one call. */
export function logError(tag: string, error: unknown, context?: SafeLogContext): void {
  if (context) console.error(tag, logSafeError(error), logSafeContext(context));
  else console.error(tag, logSafeError(error));
}

/** Same as logError at warn level. */
export function logWarn(tag: string, error: unknown, context?: SafeLogContext): void {
  if (context) console.warn(tag, logSafeError(error), logSafeContext(context));
  else console.warn(tag, logSafeError(error));
}
